/* eslint-disable @typescript-eslint/no-explicit-any */
/*
 * AdvaHealth Solutions Pty. Ltd. ("AHS") CONFIDENTIAL
 * Copyright (c) 2024 AdvaHealth Solutions Pty. Ltd. All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains the property of AHS. The intellectual and technical concepts contained
 * herein are proprietary to AHS and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material is strictly forbidden unless prior written permission is obtained
 * from AHS.  Access to the source code contained herein is hereby forbidden to anyone except current AHS employees, managers or contractors who have executed
 * Confidentiality and Non-disclosure agreements explicitly covering such access.
 *
 * The copyright notice above does not evidence any actual or intended publication or disclosure  of  this source code, which includes
 * information that is confidential and/or proprietary, and is a trade secret, of AHS. ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC  PERFORMANCE,
 * OR PUBLIC DISPLAY OF OR THROUGH USE  OF THIS  SOURCE CODE  WITHOUT  THE EXPRESS WRITTEN CONSENT OF COMPANY IS STRICTLY PROHIBITED, AND IN VIOLATION OF APPLICABLE
 * LAWS AND INTERNATIONAL TREATIES.  THE RECEIPT OR POSSESSION OF  THIS SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS
 * TO REPRODUCE, DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT  MAY DESCRIBE, IN WHOLE OR IN PART.
 */
import React, { FC, useEffect, useMemo, useState } from "react";
import { ddMMyyyy } from "../../constant";
import { BigText } from "../Displays/BigText";
import { IStudyOrderResponse } from "../../types/patientPortal";
import { formatValidDate } from "../../helpers";
import { StudyMultiLevelDropdown } from "../Dropdown/StudyMultiLevelDropdown";
import { calculateRemainingMinutes } from "../../utils/date";
import { convertUnitTImeMinutesToString } from "../../utils/format";

interface IExamCard {
  study?: IStudyOrderResponse;
  dateFormat?: string;
  plainDateFormat?: string;
  isMobile?: boolean;
  isMobileOnly?: boolean;
  download?: () => void;
  onNavigate?: (type: string) => void;
  reportIcon?: any;
  imageIcon?: any;
  downloadIcon?: any;
  infoIcon?: any;
  clockIcon?: any;
  warnIcon?: any;
}

export const ExamCard: FC<IExamCard> = ({
  study,
  dateFormat = ddMMyyyy,
  plainDateFormat = ddMMyyyy,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  isMobile,
  isMobileOnly,
  download,
  reportIcon,
  imageIcon,
  downloadIcon,
  infoIcon,
  clockIcon,
  warnIcon,
  onNavigate,
}) => {
  const mobileLayout = useMemo(() => {
    return isMobile || isMobileOnly;
  }, [isMobile, isMobileOnly]);

  const [showGroupBtns, setShowGroupBtns] = useState<boolean>(false);
  const dropdownItems = [
    {
      name: "Download Report",
      iconImage: <img className="m-r-xxs" src={downloadIcon} />,
      onHandleClick: () => download(),
      show: true,
      disabled:
        !(
          study?.hasReport &&
          calculateRemainingMinutes(study?.reportAvailableAt) <= 0
        ) || calculateRemainingMinutes(study?.reportAvailableUntil) < 0,
    },
  ];
  const [imagesAvailableAt, setImagesAvailableAt] = useState(0);
  const [reportAvailableAt, setReportAvailableAt] = useState(0);
  const imageUntilMinutes = calculateRemainingMinutes(
    study?.imagesAvailableUntil
  );
  const reportUntilMinutes = calculateRemainingMinutes(
    study?.reportAvailableUntil
  );
  useEffect(() => {
    const imageMinutes = calculateRemainingMinutes(study?.imagesAvailableAt);
    const reportMinutes = calculateRemainingMinutes(study?.reportAvailableAt);
    setImagesAvailableAt(imageMinutes);
    setReportAvailableAt(reportMinutes);
    let imageInterval: NodeJS.Timeout;
    let reportInterval: NodeJS.Timeout;
    if (imageMinutes > 0) {
      imageInterval = setInterval(() => {
        setImagesAvailableAt((prev) => {
          const newMinutes = prev - 1;
          if (newMinutes > 0) return newMinutes;
          clearInterval(imageInterval);
          return 0;
        });
      }, 60000);
    }
    if (reportMinutes > 0) {
      reportInterval = setInterval(() => {
        setReportAvailableAt((prev) => {
          const newMinutes = prev - 1;
          if (newMinutes > 0) return newMinutes;
          clearInterval(reportInterval);
          return 0;
        });
      }, 60000);
    }
    // Clear intervals when component unmounts or item changes
    return () => {
      clearInterval(imageInterval);
      clearInterval(reportInterval);
    };
  }, [study]);

  const getImageInfoMessage = useMemo(() => {
    if (imagesAvailableAt > 0) {
      return (
        <div className="available-studies line-background p-xs b-r-sm d-flex align-items-center m-b-xxs">
          {infoIcon}
          <span className="tex-label">
            Images will be available in{" "}
            {convertUnitTImeMinutesToString(imagesAvailableAt)}
          </span>
        </div>
      );
    }
    if (study?.hasImages && imageUntilMinutes != 0) {
      if (imageUntilMinutes > 0) {
        return (
          <div className="bg-available p-xs b-r-sm d-flex align-items-center m-b-xxs">
            {clockIcon}
            <span className="text-label-available">
              Images are available until{" "}
              {formatValidDate(study?.imagesAvailableUntil, plainDateFormat)}
            </span>
          </div>
        );
      } else {
        return (
          <div className="alert line-background p-xs b-r-sm d-flex align-items-center m-b-xxs">
            {warnIcon}
            <span className="tex-label">
              Images were available until{" "}
              {formatValidDate(study?.imagesAvailableUntil, plainDateFormat)}
            </span>
          </div>
        );
      }
    }
    return null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [imagesAvailableAt, infoIcon, clockIcon]);

  const getReportInfoMessage = useMemo(() => {
    if (reportAvailableAt > 0) {
      return (
        <div className="available-studies bg-available p-xs b-r-sm d-flex align-items-center m-b-xxs">
          {infoIcon}
          <span className="text-label-fixed">
            Report will be available in{" "}
            {convertUnitTImeMinutesToString(reportAvailableAt)}
          </span>
        </div>
      );
    }
    if (study?.hasReport && reportUntilMinutes != 0) {
      if (reportUntilMinutes > 0) {
        return (
          <div className="bg-available p-xs b-r-sm d-flex align-items-center m-b-xxs">
            {clockIcon}
            <span className="text-label-available">
              Report is available until{" "}
              {formatValidDate(study?.reportAvailableUntil, plainDateFormat)}
            </span>
          </div>
        );
      } else {
        return (
          <div className="alert line-background p-xs b-r-sm d-flex align-items-center m-b-xxs">
            {warnIcon}
            <span className="text-label">
              Report was available until{" "}
              {formatValidDate(study?.reportAvailableUntil, plainDateFormat)}
            </span>
          </div>
        );
      }
    }
    return null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportAvailableAt, infoIcon, clockIcon]);

  return (
    <>
      <h5 className="font-weight m-b-xs">{study?.description}</h5>

      <div className="details">
        {getImageInfoMessage} {getReportInfoMessage}
        <div className="study-details d-flex align-items-between w-100">
          <div className="study-info flex-1 w-100">
            <div className="d-flex date m-b-xxs w-100">
              <span className="text-label-fixed exam">Date</span>
              <BigText
                maxWidth={"calc(100% - 80px)"}
                text={formatValidDate(study?.date, dateFormat)}
              />
            </div>

            <div className="d-flex date m-b-xxs w-100">
              <span className="text-label-fixed exam">Referrer</span>
              <span>{study.referrer}</span>
            </div>

            <div className="d-flex date m-b-xxs w-100">
              <span className="text-label-fixed exam">Reported By</span>
              <span>{study.reportedBy}</span>
            </div>
          </div>
          {!mobileLayout && (onNavigate || download) && (
            <div className="study-action d-flex flex-end-between">
              <button
                disabled={
                  !study?.hasReport ||
                  reportAvailableAt > 0 ||
                  reportUntilMinutes < 0
                }
                className="btn btn-primary btn-fixed-width flex-center"
                onClick={() => onNavigate("report")}
              >
                <img
                  className="m-r-xxs padding-b-3px"
                  src={reportIcon}
                  alt="Report Icon"
                />
                Report
              </button>
              <button
                disabled={
                  imagesAvailableAt > 0 ||
                  !study.hasImages ||
                  imageUntilMinutes < 0
                }
                className="btn btn-primary btn-fixed-width m-l-xs flex-center"
                onClick={() => onNavigate("images")}
              >
                <img
                  className="m-r-xs padding-b-3px"
                  src={imageIcon}
                  alt="Report Icon"
                />
                Images
              </button>

              <button
                className="btn btn-light d-flex  flex-center m-l-xs"
                onClick={download}
                disabled={
                  !study?.hasReport ||
                  reportAvailableAt > 0 ||
                  reportUntilMinutes < 0
                }
              >
                <img className="m-r-xs" src={downloadIcon} alt="Report Icon" />
                <BigText text="Download Report" maxWidth={125} />
              </button>
            </div>
          )}
        </div>
        {mobileLayout && (onNavigate || download) && (
          <div className="study-action m-t-xs w-100">
            <div className="d-flex align-items-center ">
              <button
                className="btn btn-primary flex-1 m-r-xxs d-flex  flex-center"
                disabled={
                  !study?.hasReport ||
                  reportAvailableAt > 0 ||
                  reportUntilMinutes < 0
                }
                onClick={() => onNavigate("report")}
              >
                <img
                  className="m-r-xs padding-b-3px"
                  src={reportIcon}
                  alt="Report Icon"
                />
                Report
              </button>
              <button
                disabled={
                  imagesAvailableAt > 0 ||
                  !study.hasImages ||
                  imageUntilMinutes < 0
                }
                className="btn btn-primary flex-1 d-flex  flex-center"
                onClick={() => onNavigate("images")}
              >
                <img
                  className="m-r-xs padding-b-3px"
                  src={imageIcon}
                  alt="Report Icon"
                />
                Images
              </button>
              {isMobile && !isMobileOnly && (
                <button
                  className="btn btn-light d-flex  flex-center m-l-xs"
                  onClick={download}
                  disabled={
                    !study?.hasReport ||
                    reportAvailableAt > 0 ||
                    reportUntilMinutes < 0
                  }
                >
                  <img
                    className="m-r-xs"
                    src={downloadIcon}
                    alt="Report Icon"
                  />
                  <BigText text="Download Report" maxWidth={125} />
                </button>
              )}
              {isMobileOnly && (
                <StudyMultiLevelDropdown
                  showButton
                  wrapperClass="m-l-xxs"
                  data={study}
                  position="bottom"
                  showSubItem
                  size="sm"
                  onClose={() => {
                    setShowGroupBtns(false);
                  }}
                  show={showGroupBtns}
                  onHandleClick={() => {
                    setShowGroupBtns(!showGroupBtns);
                  }}
                  items={dropdownItems}
                />
              )}
            </div>
          </div>
        )}
      </div>
    </>
  );
};
